import { i18n } from '@/plugins/i18n';
import { ROLES, type ApiV1, type ApiV2, type Role } from '@teamworksdev/influencer/core';
import { isNil } from 'lodash-es';

export const adminRoles: Readonly<Role[]> = [
	ROLES.SUPER_ADMINISTRATOR,
	ROLES.ADMINISTRATOR,
	ROLES.CLIENT_SUCCESS,
] as const;

export function isAdminRole(role: Role) {
	return adminRoles.includes(role);
}

export function getRoleName(role: Role): string {
	const roleMap = new Map<Role, string>([
		[1, i18n.global.t('userProfileRoles.superAdmin')],
		[2, i18n.global.t('userProfileRoles.admin')],
		[3, i18n.global.t('userProfileRoles.clientSuccess')],
		[4, i18n.global.t('userProfileRoles.teamUser')],
		[5, i18n.global.t('userProfileRoles.complianceOfficer')],
		[6, i18n.global.t('userProfileRoles.agent')],
		[7, i18n.global.t('userProfileRoles.brandUser')],
	]);
	const roleName = roleMap.get(role);
	if (!roleName) {
		console.warn('unknown role');
		return i18n.global.t('unknown');
	}
	return roleName;
}

export function isUserV2(
	user: ApiV1.User | ApiV2.User | undefined | null,
): user is ApiV2.User {
	if (isNil(user)) return false;
	return !('user_profiles' in user);
}

export function isUserProfileV2(
	profile: ApiV1.UserProfile | ApiV1.FullUserProfile | undefined | null,
): profile is ApiV1.FullUserProfile {
	if (isNil(profile)) return false;
	return 'data' in profile;
}

export function adaptUser(
	user: ApiV1.User | ApiV2.User,
): ApiV2.User {
	if (isUserV2(user)) return user;

	return {
		id: 0,
		uuid: user.uuid,
		email: user.email,
		first_name: user.first_name,
		last_name: user.last_name,
		phone: user.phone,
		profile_image: user.image,
		external_profile_url: user.external_profile_url,
		is_teamworks_assigned: user.teamworks_assigned,
		has_okta_accounts: user.has_okta_accounts,
		terms_of_use: user.terms_of_use,
	};
}

export function adaptUserProfile(profile: ApiV1.UserProfile | ApiV1.FullUserProfile): ApiV1.FullUserProfile {
	if (isUserProfileV2(profile)) return profile;

	switch (profile.role_id) {
		case ROLES.AGENT: {
			const athlete = profile.athlete;
			const { school, team, group } = profile.athlete.academic_profile;
			return {
				role_id: profile.role_id,
				uuid: profile.uuid,
				is_default: profile.is_default,
				axle_data: profile.axle,
				global_exchange_opt_in: profile.global_exchange_opt_in,
				data: {
					athlete: {
						uuid: athlete.uuid,
						first_name: athlete.first_name,
						last_name: athlete.last_name,
						profile_image: athlete.image,
						jersey_number: athlete.academic_profile.jersey_number,
						position: athlete.academic_profile.position,
						local_exchange_opt_in: athlete.academic_profile.local_exchange_opt_in,
						school: {
							...school,
							is_local_exchange_enabled: school.local_exchange_enabled,
							include_school_resources: false, // TODO: investigate this
							show_nil_go_status: false, // TODO: investigate this
						},
						team: {
							...team,
							services: [
								{ name: 'brand_strategy', is_enabled: team.services.brand_strategy },
								{ name: 'data_services', is_enabled: team.services.data_services },
							],
						},
						group,
					},
				},
			};
		}
		case ROLES.BRAND_USER: {
			const brand = profile.exchange_brand;
			return {
				role_id: profile.role_id,
				uuid: profile.uuid,
				is_default: profile.is_default,
				axle_data: profile.axle,
				global_exchange_opt_in: profile.global_exchange_opt_in,
				data: {
					user_type: profile.exchange_brand_user_type,
					exchange_brand: {
						...brand,
						active_bulletins_allowed: brand.active_bulletins_allowed ?? 0,
						leaderboard: {
							is_enabled: brand.leaderboard.enabled,
							unit_type: brand.leaderboard.unit_type,
						},
					},
				},
			};
		}
		case ROLES.COMPLIANCE_OFFICER: {
			const school = profile.school;
			return {
				role_id: profile.role_id,
				uuid: profile.uuid,
				is_default: profile.is_default,
				axle_data: profile.axle,
				global_exchange_opt_in: profile.global_exchange_opt_in,
				data: {
					school: {
						...school,
						is_local_exchange_enabled: school.local_exchange_enabled,
						include_school_resources: false, // TODO: investigate this
						show_nil_go_status: false, // TODO: investigate this
					},
				},
			};
		}
		case ROLES.TEAM_USER: {
			const { school, team } = profile.academic_profile;
			return {
				role_id: profile.role_id,
				uuid: profile.uuid,
				is_default: profile.is_default,
				axle_data: profile.axle,
				global_exchange_opt_in: profile.global_exchange_opt_in,
				data: {
					...profile.academic_profile,
					school: {
						...school,
						is_local_exchange_enabled: school.local_exchange_enabled,
						include_school_resources: false, // TODO: investigate this
						show_nil_go_status: false, // TODO: investigate this
					},
					team: {
						...team,
						services: [
							{ name: 'brand_strategy', is_enabled: team.services.brand_strategy },
							{ name: 'data_services', is_enabled: team.services.data_services },
						],
					},
				},
			};
		}
		case ROLES.CLIENT_SUCCESS:
		case ROLES.ADMINISTRATOR:
		case ROLES.SUPER_ADMINISTRATOR: {
			return {
				role_id: profile.role_id,
				uuid: profile.uuid,
				is_default: profile.is_default,
				axle_data: profile.axle,
				global_exchange_opt_in: profile.global_exchange_opt_in,
				data: null,
			};
		}
	}
}
